import React, { useCallback, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, CreditCard, RefreshCw } from 'lucide-react'
import { BILLING_PAYMENT_CONFIRM_ENABLED, BILLING_PAYMENT_PROVIDER, ENABLE_BILLING_COUPONS } from '../config/featureFlags'
import {
  billingParamsFromCheckoutProduct,
  checkoutProducts,
  checkoutTierGroups,
  formatKrw,
  organizationContractPlan,
} from '../content/publicPlans'
import { api, getApiErrorMessage } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { formatBillingStatusLabel } from '../lib/billingLabels'
import { useToast } from '../ui/ToastProvider'

function resolveProductIdFromQuery(params) {
  const raw = params.get('checkoutProduct') || params.get('product')
  if (raw && checkoutProducts.some((p) => p.id === raw)) return raw
  return checkoutProducts.find((p) => p.id === 'basic-1m')?.id || checkoutProducts[0]?.id || ''
}

function OrganizationContractSubscribeCard({ plan }) {
  if (!plan) return null
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" aria-labelledby="billing-subscribe-organization-contract">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="billing-subscribe-organization-contract" className="text-base font-bold text-slate-950">{plan.name}</h2>
        {plan.badge ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{plan.badge}</span> : null}
      </div>
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-slate-500">{plan.period}</p>
            <div className="mt-2 text-2xl font-black text-slate-950">{plan.price}</div>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">{plan.desc}</p>
          </div>
          <Link
            to={plan.cta.to}
            className="inline-flex min-h-[2.5rem] items-center justify-center rounded border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-white"
          >
            {plan.cta.label}
          </Link>
        </div>
      </div>
    </section>
  )
}

export default function BillingSubscribePage() {
  const { show } = useToast()
  const [searchParams] = useSearchParams()
  const [selectedId, setSelectedId] = useState(() => resolveProductIdFromQuery(searchParams))
  const [couponCode, setCouponCode] = useState(() => (searchParams.get('prefillCoupon') || searchParams.get('coupon') || '').trim().toUpperCase())
  const [quote, setQuote] = useState(null)
  const [quoteError, setQuoteError] = useState('')
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [latestOrder, setLatestOrder] = useState(null)

  const selected = useMemo(
    () => checkoutProducts.find((p) => p.id === selectedId) || checkoutProducts[0],
    [selectedId]
  )
  const selectedBillingParams = useMemo(() => billingParamsFromCheckoutProduct(selected), [selected])
  const quoteFinalPrice = typeof quote?.finalPrice === 'number' ? quote.finalPrice : Number(selected?.supplyPriceKrw || 0)
  const quoteBasePrice = typeof quote?.basePrice === 'number' ? quote.basePrice : Number(selected?.supplyPriceKrw || 0)

  const fetchQuote = async () => {
    if (!selected) return
    setQuoteError('')
    setQuoteLoading(true)
    try {
      const res = await api.post('/billing/quote', {
        ...selectedBillingParams,
        couponCode: couponCode.trim() || undefined,
      })
      setQuote(res?.data?.data ?? null)
    } catch (e) {
      setQuote(null)
      setQuoteError(getApiErrorMessage(e, '결제 예정 금액을 확인하지 못했습니다.'))
    } finally {
      setQuoteLoading(false)
    }
  }

  const createOrder = async () => {
    if (!selected) return
    try {
      setActionLoading(true)
      const res = await api.post('/billing/orders', {
        ...selectedBillingParams,
        paymentMethod: 'CARD',
        couponCode: couponCode.trim() || undefined,
      })
      const order = res?.data?.data ?? null
      setLatestOrder(order)
      show('주문이 생성되었습니다.', 'success')
    } catch (e) {
      show(getApiErrorMessage(e, '주문 생성에 실패했습니다.'), 'error')
    } finally {
      setActionLoading(false)
    }
  }

  const confirmPayment = useCallback(async () => {
    if (!latestOrder?.id) return
    if (!BILLING_PAYMENT_CONFIRM_ENABLED) {
      show('웹 결제 연동이 아직 준비되지 않았습니다.', 'error')
      return
    }
    try {
      setActionLoading(true)
      await api.post('/billing/payments/confirm', {
        orderId: latestOrder.id,
        provider: BILLING_PAYMENT_PROVIDER,
        paymentMethod: 'CARD',
      })
      show('결제가 확정되었습니다.', 'success')
      setLatestOrder((prev) => prev ? { ...prev, status: 'PAID', paidAt: new Date().toISOString() } : prev)
    } catch (e) {
      show(getApiErrorMessage(e, '결제 확정에 실패했습니다.'), 'error')
    } finally {
      setActionLoading(false)
    }
  }, [latestOrder, show])

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-brand-sky">개인 구독</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950">구독하기</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/billing"
            className="inline-flex min-h-[2.5rem] items-center justify-center rounded border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            내 구독
          </Link>
          <Link
            to="/billing/orders"
            className="inline-flex min-h-[2.5rem] items-center justify-center rounded border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            결제내역
          </Link>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-5">
          {checkoutTierGroups.map((tier) => (
            <section key={tier.id} className="rounded-lg border border-slate-200 bg-white p-5" aria-labelledby={`billing-subscribe-${tier.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <h2 id={`billing-subscribe-${tier.id}`} className="text-base font-bold text-slate-950">{tier.title}</h2>
                {tier.badge ? <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-brand-navy ring-1 ring-sky-200">{tier.badge}</span> : null}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {tier.products.map((product) => {
                  const active = product.id === selected?.id
                  return (
                    <button
                      key={product.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        setSelectedId(product.id)
                        setQuote(null)
                        setQuoteError('')
                      }}
                      className={`flex min-h-[9.5rem] w-full flex-col rounded-lg border p-4 text-left transition ${
                        active
                          ? 'border-brand-sky bg-sky-50 ring-2 ring-sky-100'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-bold text-slate-950">
                            {product.periodLabel === '12개월' ? '12개월 선결제' : '1개월 이용'}
                          </div>
                          <div className="mt-1 text-xs leading-5 text-slate-600">{product.summary}</div>
                        </div>
                        {active ? <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-navy" aria-hidden="true" /> : null}
                      </div>
                      <div className="mt-auto pt-4">
                        {product.listPriceKrw != null && Number(product.listPriceKrw) > Number(product.supplyPriceKrw || 0) ? (
                          <div className="text-sm font-medium text-slate-400 line-through">{formatKrw(product.listPriceKrw)}</div>
                        ) : null}
                        <div className="text-2xl font-black text-slate-950">{formatKrw(product.supplyPriceKrw)}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
          <OrganizationContractSubscribeCard plan={organizationContractPlan} />
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:h-fit">
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="text-base font-bold text-slate-950">주문 요약</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <SummaryLine label="선택" value={selected?.name || '-'} />
              <SummaryLine label="이용 기간" value={selected?.periodLabel || '-'} />
              <SummaryLine label="기본 금액" value={formatKrw(quoteBasePrice)} />
              {quote?.adjustmentLabel ? <SummaryLine label="적용 정책" value={quote.adjustmentLabel} /> : null}
              {quote?.coupon?.applied && typeof quote.coupon.discountAmount === 'number' ? (
                <SummaryLine label="쿠폰 할인" value={`-${formatKrw(quote.coupon.discountAmount)}`} accent />
              ) : null}
              <div className="flex justify-between gap-4 border-t border-slate-200 pt-3 text-base font-black text-slate-950">
                <dt>결제 예정 금액</dt>
                <dd className="text-right">{formatKrw(quoteFinalPrice)}</dd>
              </div>
            </dl>

            {ENABLE_BILLING_COUPONS ? (
              <div className="mt-5">
                <label htmlFor="billing-subscribe-coupon" className="block text-xs font-semibold text-slate-600">쿠폰 코드</label>
                <div className="mt-1 flex gap-2">
                  <input
                    id="billing-subscribe-coupon"
                    type="text"
                    autoComplete="off"
                    placeholder="쿠폰 코드를 입력하세요"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase())
                      setQuote(null)
                    }}
                    className="min-w-0 flex-1 rounded border border-slate-200 px-3 py-2 text-sm outline-none ring-sky-200 focus:border-sky-400 focus:ring-2"
                  />
                  <button
                    type="button"
                    onClick={fetchQuote}
                    disabled={quoteLoading}
                    className="inline-flex min-h-[2.5rem] items-center gap-1 rounded border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                    title="결제 예정 금액 확인"
                  >
                    <RefreshCw className={`h-4 w-4 ${quoteLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                    확인
                  </button>
                </div>
                {quoteError ? <p className="mt-2 text-xs text-red-600">{quoteError}</p> : null}
                {quote?.coupon?.reason && !quote?.coupon?.applied ? <p className="mt-2 text-xs text-amber-800">{quote.coupon.reason}</p> : null}
              </div>
            ) : null}

            <button
              type="button"
              onClick={createOrder}
              disabled={actionLoading}
              className="mt-5 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded bg-brand-navy px-4 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
            >
              <CreditCard className="h-4 w-4" aria-hidden="true" />
              주문 생성
            </button>
          </div>

          {latestOrder ? (
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="text-base font-bold text-slate-950">최근 주문</h2>
              <dl className="mt-3 space-y-2 text-sm text-slate-700">
                <SummaryLine label="주문번호" value={latestOrder.orderNo || latestOrder.id || '-'} />
                <SummaryLine label="상태" value={formatBillingStatusLabel(latestOrder.status, 'order')} />
                <SummaryLine label="금액" value={formatKrw(latestOrder.amount)} />
                <SummaryLine label="일시" value={formatDateTimeMinute(latestOrder.paidAt || latestOrder.createdAt)} />
              </dl>
              {(latestOrder.status === 'CREATED' || latestOrder.status === 'PAYMENT_PENDING') ? (
                <button
                  type="button"
                  onClick={confirmPayment}
                  disabled={actionLoading || !BILLING_PAYMENT_CONFIRM_ENABLED}
                  title={!BILLING_PAYMENT_CONFIRM_ENABLED ? '웹 결제 연동 준비 중입니다.' : undefined}
                  className="mt-4 inline-flex min-h-[2.5rem] w-full items-center justify-center gap-2 rounded border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                >
                  <CreditCard className="h-4 w-4" aria-hidden="true" />
                  {BILLING_PAYMENT_CONFIRM_ENABLED ? '결제 확정' : '결제 준비 중'}
                </button>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>
    </section>
  )
}

function SummaryLine({ label, value, accent = false }) {
  return (
    <div className={`flex justify-between gap-4 ${accent ? 'text-emerald-800' : 'text-slate-600'}`}>
      <dt>{label}</dt>
      <dd className={`text-right ${accent ? 'font-semibold' : 'font-medium text-slate-950'}`}>{value}</dd>
    </div>
  )
}
